/**
 * ConfigBlobSync<T> — Generic CAS-retry sync for config-type blobs.
 *
 * Handles: key derivation, encrypt/decrypt, commit signing, CAS loop.
 * Used for: workspace registry (_registry~{kid}), MCP config blobs.
 *
 * The CAS retry loop implements a CvRDT sync pattern:
 *   pull(remote) → merge(local, remote) → push(merged) → retry on CAS mismatch
 */

import { sha256 as nobleSha256 } from '@noble/hashes/sha2'
import { hkdf as nobleHkdf } from '@noble/hashes/hkdf'
import { WebCryptoProvider } from './providers'
import { HttpStoreBackend, type StoreBackend } from './storeBackends'
import { BlobRepositoryImpl } from './blobRepository'
import { packFileContent, resolveContentBytes, type ContentCodecDeps } from './contentCodec'
import { encryptState, decryptState, buildAad, type WorkspaceState } from './stateCodec'
import { buildTreeFromPaths, resolvePath, type DirEntryFile } from './dirTreeCodec'
import { buildSignedCommit, base64UrlEncode } from './commitSigning'
import type { MergeStrategy } from './registryMerge'
import { createWorkspaceAuthProvider, bootstrapWorkspace } from '@secureblob/sdk/workspaceAuth'
import { decodeWorkspaceSecretV2 } from '@secureblob/sdk/workspaceSecretV2'

// ── Helpers ────────────────────────────────────────────────────

/** JSON.stringify with recursively sorted keys — canonical output for deep equality (§5.9). */
export function stableStringify(obj: unknown): string {
    if (obj === null || typeof obj !== 'object') return JSON.stringify(obj)
    if (Array.isArray(obj)) return '[' + obj.map(stableStringify).join(',') + ']'
    const sorted = Object.keys(obj as Record<string, unknown>).sort()
    return '{' + sorted.map(k =>
        JSON.stringify(k) + ':' + stableStringify((obj as Record<string, unknown>)[k])
    ).join(',') + '}'
}


// ── Key derivation ─────────────────────────────────────────────

export interface ConfigBlobKeys {
    blobId: string
    readKey: Uint8Array
    writerSeed: Uint8Array
    /** Encoded workspace-secret-v2 (Spec §4.1). Used as auth bearer source. */
    workspaceSecret: string
}

/** Canonical JSON stringify (sorted keys, no undefined values). */
function canonicalJsonStringify(value: unknown): string {
    if (value === null) return 'null'
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        return JSON.stringify(value)
    }
    if (Array.isArray(value)) {
        return `[${value.map(item => canonicalJsonStringify(item)).join(',')}]`
    }
    if (typeof value !== 'object') {
        throw new TypeError('Unsupported value in canonical JSON serialization')
    }
    const entries = Object.entries(value as Record<string, unknown>)
        .filter(([, v]) => v !== undefined)
        .sort(([a], [b]) => a < b ? -1 : a > b ? 1 : 0)
    return `{${entries.map(([k, v]) => `${JSON.stringify(k)}:${canonicalJsonStringify(v)}`).join(',')}}`
}

/**
 * Derive blobId, read key, writer seed, reader seed, and workspace-secret-v2
 * for a config blob. Uses HKDF from primarySeed.
 *
 * Spec §8.9: deterministic blobs MUST use full workspace-secret-v2
 * including readerSeed, with a dedicated HKDF info string.
 */
export function deriveRegistryKeys(primarySeed: Uint8Array, primaryKid: string): ConfigBlobKeys {
    const blobId = `_registry~${primaryKid}`
    const salt = new TextEncoder().encode(`secureblob:${blobId}`)

    const readKey = nobleHkdf(nobleSha256, primarySeed, salt,
        new TextEncoder().encode('secureblob-registry-read-v1'), 32)

    const writerSeed = nobleHkdf(nobleSha256, primarySeed, salt,
        new TextEncoder().encode('secureblob-registry-write-v1'), 32)

    // Spec §8.9: deterministic readerSeed via own HKDF info string
    const readerSeed = nobleHkdf(nobleSha256, primarySeed, salt,
        new TextEncoder().encode('secureblob-registry-reader-v2'), 32)

    // Build workspace-secret-v2 (Spec §4.1)
    const secret = {
        v: 2,
        type: 'workspace-secret',
        blobId,
        readKey: base64UrlEncode(readKey),
        readerSeed: base64UrlEncode(readerSeed),
        writerSeed: base64UrlEncode(writerSeed),
    }
    const secretJson = canonicalJsonStringify(secret)
    const workspaceSecret = base64UrlEncode(new TextEncoder().encode(secretJson))

    return { blobId, readKey, writerSeed, workspaceSecret }
}

/**
 * Derive workspace-scoped encryption keys from the capability read key.
 * Matches deriveWorkspaceKey() in realWorkspace.ts.
 */
function deriveKey(readKey: Uint8Array, blobId: string, scope: 'state' | 'file'): Uint8Array {
    const salt = new TextEncoder().encode(`secureblob:${blobId}`)
    const info = new TextEncoder().encode(`secureblob-${scope}-v1`)
    return nobleHkdf(nobleSha256, readKey, salt, info, 32)
}

// ── ConfigBlobSync ─────────────────────────────────────────────

export interface SyncResult<T> {
    state: T
    head: string | null
    synced: boolean
}

const MAX_CAS_RETRIES = 3

export class ConfigBlobSync<T> {
    private crypto = new WebCryptoProvider()
    private syncMutex: Promise<void> = Promise.resolve()
    /** §5.8: Reusable HTTP backend (stateless — safe to share) */
    private readonly remote: HttpStoreBackend
    /** §5.8: Last successful remote head hash */
    private lastKnownHead: string | null = null
    /** §5.8: Cached remote state corresponding to lastKnownHead */
    private lastKnownState: T | null = null
    /** Deduplicate bootstrap attempts when a fresh profile races its first syncs. */
    private bootstrapPromise: Promise<void> | null = null

    constructor(
        private blobId: string,
        private readKey: Uint8Array,
        private writerSeed: Uint8Array,
        private workspaceSecret: string,
        private apiBaseUrl: string,
        private strategy: MergeStrategy<T>,
        private filePath = '/registry.json',
    ) {
        // Spec §8.9: ConfigBlobSync uses v2 auth transport (challenge-signed access tokens)
        // instead of sending raw capability as Bearer.
        const authProvider = createWorkspaceAuthProvider(apiBaseUrl, workspaceSecret, blobId)
        this.remote = new HttpStoreBackend(apiBaseUrl, workspaceSecret, blobId, authProvider ?? undefined)
    }

    // ── Public API ─────────────────────────────────────────────

    /**
     * Pull current state from remote.
     * Returns null if blob doesn't exist yet.
     */
    async pull(): Promise<{ state: T; head: string } | null> {
        const { heads } = await this.getRemoteHeadsWithBootstrapRecovery()
        if (!heads || heads.length === 0) return null

        const head = heads[0]

        // §5.8: Head-check short-circuit
        if (head === this.lastKnownHead && this.lastKnownState !== null) {
            return { state: this.lastKnownState, head }
        }

        const result = await this.fetchAndDecryptState(head)
        if (result.ok) {
            this.lastKnownHead = head
            this.lastKnownState = result.state
        }
        return { state: result.state, head }
    }

    /**
     * Full CAS-retry sync: pull → merge → push.
     * Returns merged state and whether the push succeeded.
     * Mutex-protected to prevent concurrent sync operations.
     */
    async sync(localState: T | (() => T)): Promise<SyncResult<T>> {
        // Serialize concurrent sync calls
        const result = new Promise<SyncResult<T>>((resolve, reject) => {
            this.syncMutex = this.syncMutex.then(async () => {
                try {
                    resolve(await this.syncInternal(localState))
                } catch (e) {
                    reject(e)
                }
            })
        })
        return result
    }

    private async syncInternal(localState: T | (() => T)): Promise<SyncResult<T>> {
        // §5.7: Resolve getter lazily — read local state at merge-time, not sync-start
        const resolveLocal = (): T => typeof localState === 'function' ? (localState as () => T)() : localState
        let attempts = 0

        while (attempts < MAX_CAS_RETRIES) {
            // 1. Fetch remote head
            const { heads } = await this.getRemoteHeadsWithBootstrapRecovery()
            console.log(`[ConfigSync] sync blob=${this.blobId.slice(0, 16)} attempt=${attempts + 1} remoteHead=${heads[0]?.slice(0, 8) ?? 'none'}`)

            if (!heads || heads.length === 0) {
                // Bootstrap: no remote blob exists yet
                try {
                    const resolved = resolveLocal()
                    const head = await this.commitState(resolved, [])
                    // §5.8: Cache the bootstrapped state
                    this.lastKnownHead = head
                    this.lastKnownState = resolved
                    return { state: resolved, head, synced: true }
                } catch {
                    // Bootstrap race — another device created it first
                    attempts++
                    continue
                }
            }

            const remoteHead = heads[0]

            // §5.8: Head-check short-circuit — skip fetch+decrypt if remote hasn't changed
            let remoteState: T
            if (remoteHead === this.lastKnownHead && this.lastKnownState !== null) {
                remoteState = this.lastKnownState
            } else {
                // 2. Fetch + decrypt remote state
                const result = await this.fetchAndDecryptState(remoteHead)
                remoteState = result.state
                // §5.8: Only cache on successful decrypt (not AEAD corruption fallbacks)
                if (result.ok) {
                    this.lastKnownHead = remoteHead
                    this.lastKnownState = remoteState
                }
            }

            // 3. Merge (resolve local state NOW — after fetch — to capture concurrent mutations)
            const merged = this.strategy.merge(resolveLocal(), remoteState)

            // 4. Check if push needed (§5.9: canonical deep comparison via stableStringify)
            const remoteJson = stableStringify(remoteState)
            const mergedJson = stableStringify(merged)

            if (mergedJson === remoteJson) {
                // No local changes to push — remote is already merged
                console.log(`[ConfigSync] no-op — merged === remote, skipping push`)
                return { state: merged, head: remoteHead, synced: true }
            }
            console.log(`[ConfigSync] merge produced diff — pushing with base=[${remoteHead.slice(0, 8)}]`)

            // 5. Push merged state
            try {
                const head = await this.commitState(merged, [remoteHead])
                // §5.8: Cache the pushed state
                this.lastKnownHead = head
                this.lastKnownState = merged
                return { state: merged, head, synced: true }
            } catch (e) {
                if (e instanceof Error && e.message.includes('CAS_MISMATCH')) {
                    // CAS conflict — retry (cache might be stale, next iteration re-fetches)
                    console.warn(`[ConfigSync] CAS_MISMATCH on push — retrying (attempt ${attempts + 1}/${MAX_CAS_RETRIES})`)
                    // §5.8: Invalidate cache so next iteration re-fetches from server
                    this.lastKnownHead = null
                    this.lastKnownState = null
                    attempts++
                    continue
                }
                // OBJECT_MISSING: server lost parent objects — re-push without parents (bootstrap)
                if (e instanceof Error && e.message.includes('OBJECT_MISSING')) {
                    try {
                        const head = await this.commitState(merged, [])
                        // §5.8: Cache the bootstrapped state
                        this.lastKnownHead = head
                        this.lastKnownState = merged
                        return { state: merged, head, synced: true }
                    } catch {
                        // Bootstrap also failed — fall through to throw original error
                    }
                }
                throw e
            }
        }

        // Exhausted retries — return local state, will retry next cycle
        console.warn(`ConfigBlobSync: CAS retry exhausted for ${this.blobId}`)
        return { state: resolveLocal(), head: null, synced: false }
    }

    private async getRemoteHeadsWithBootstrapRecovery() {
        try {
            return await this.remote.getHeads(this.blobId)
        } catch (error) {
            if (!this.shouldBootstrapAfterAuthFailure(error)) throw error
            await this.ensureServerAclBootstrapped()
            return this.remote.getHeads(this.blobId)
        }
    }

    private shouldBootstrapAfterAuthFailure(error: unknown): boolean {
        if (!(error instanceof Error)) return false
        return error.message.includes('ACL_NOT_FOUND')
            || error.message === 'Auth session failed: 404'
            || error.message.includes('Auth session failed: 404 ')
    }

    private async ensureServerAclBootstrapped(): Promise<void> {
        if (!this.bootstrapPromise) {
            this.bootstrapPromise = (async () => {
                const decoded = decodeWorkspaceSecretV2(this.workspaceSecret)
                if (!decoded) throw new Error('Failed to decode workspace secret for bootstrap recovery')
                try {
                    await bootstrapWorkspace(this.apiBaseUrl, decoded)
                } catch (error) {
                    if (error instanceof Error && error.message.includes('ACL_EXISTS')) return
                    throw error
                }
            })().finally(() => {
                this.bootstrapPromise = null
            })
        }

        await this.bootstrapPromise
    }

    // ── Private ────────────────────────────────────────────────

    /**
     * §5.8: Fetch and decrypt remote state.
     * Returns { state, ok } where ok=false means fallback to strategy.empty().
     * Callers MUST NOT cache state when ok=false (corruption guard).
     */
    private async fetchAndDecryptState(commitHash: string): Promise<{ state: T; ok: boolean }> {
        const stateKey = deriveKey(this.readKey, this.blobId, 'state')
        const fileKey = deriveKey(this.readKey, this.blobId, 'file')

        // Load commit to get stateHash
        const commitBytes = await this.remote.load(commitHash)
        if (!commitBytes) {
            // Server lost commit object — treat as empty (will merge → re-push)
            console.warn(`ConfigBlobSync: commit ${commitHash.slice(0, 8)} not found — treating as empty`)
            return { state: this.strategy.empty(), ok: false }
        }

        const commit = JSON.parse(new TextDecoder().decode(commitBytes))
        const stateHash = commit.payload?.stateHash
        if (!stateHash) throw new Error('Commit has no stateHash')

        // Load + decrypt state
        const stateCiphertext = await this.remote.load(stateHash)
        if (!stateCiphertext) {
            // Server lost state object — treat as empty (will merge → re-push)
            console.warn(`ConfigBlobSync: state ${stateHash.slice(0, 8)} not found — treating as empty`)
            return { state: this.strategy.empty(), ok: false }
        }

        let wsState: WorkspaceState
        try {
            wsState = await decryptState(stateCiphertext, { crypto: this.crypto, stateKey, blobId: this.blobId })
        } catch (e) {
            // §7.4.3 AEAD corruption — auth tag mismatch or truncated ciphertext
            console.warn(`ConfigBlobSync: AEAD corruption in state for ${this.blobId.slice(0, 12)} — treating as empty`, e)
            return { state: this.strategy.empty(), ok: false }
        }

        // Find the config file in state via dir tree
        if (!wsState.rootDirHash) {
            return { state: this.strategy.empty(), ok: false }
        }
        const dirTreeDeps = { crypto: this.crypto, stateKey, blobId: this.blobId }
        const dirStore = {
            get: (h: string) => this.remote.load(h).then(r => r ?? null),
            put: async () => { },
            has: async () => false,
        }
        const resolved = await resolvePath(this.filePath, wsState.rootDirHash, dirStore, dirTreeDeps)
        if (!resolved || resolved.entry.kind !== 'file') {
            return { state: this.strategy.empty(), ok: false }
        }
        const file = resolved.entry as DirEntryFile

        // Load + decrypt file content (handles both inline and chunked)
        const fileAad = buildAad(this.blobId, 'file')
        const contentCodecDeps: ContentCodecDeps = {
            encryptRawObject: (pt) => this.crypto.encrypt(fileKey, pt, fileAad),
            decryptRawObject: (ct) => this.crypto.decrypt(fileKey, ct, fileAad),
            encryptDeterministic: (pt) => this.crypto.encryptDeterministic(fileKey, pt, fileAad),
            hasObject: async () => false,
            sha256: (data) => this.crypto.sha256(data),
        }

        let plaintext: Uint8Array | null
        try {
            plaintext = await resolveContentBytes(
                file.content,
                { load: (h) => this.remote.load(h).then(r => r!), loadBatch: (hs) => this.remote.loadBatch(hs) },
                contentCodecDeps,
            )
        } catch (e) {
            // §7.4.3 AEAD corruption in file content
            console.warn(`ConfigBlobSync: AEAD corruption in file content for ${this.blobId.slice(0, 12)} — treating as empty`, e)
            return { state: this.strategy.empty(), ok: false }
        }
        if (!plaintext) return { state: this.strategy.empty(), ok: false }

        return { state: JSON.parse(new TextDecoder().decode(plaintext)) as T, ok: true }
    }

    private async commitState(state: T, parents: string[]): Promise<string> {
        const stateKey = deriveKey(this.readKey, this.blobId, 'state')
        const fileKey = deriveKey(this.readKey, this.blobId, 'file')

        const repo = new BlobRepositoryImpl(
            // In-memory local backend (config blobs don't need IDB persistence)
            {
                async load(): Promise<Uint8Array | null> { return null },
                async store(): Promise<void> { },
                async loadBatch(): Promise<Map<string, Uint8Array>> { return new Map() },
                async storeBatch(): Promise<void> { },
            } as StoreBackend,
            this.remote,
        )

        // 1. Encrypt file content
        const jsonStr = JSON.stringify(state, null, 2)
        const jsonBytes = new TextEncoder().encode(jsonStr)

        const fileAad = buildAad(this.blobId, 'file')
        const packed = await packFileContent(jsonBytes, {
            encryptRawObject: (pt) => this.crypto.encrypt(fileKey, pt, fileAad),
            decryptRawObject: (ct) => this.crypto.decrypt(fileKey, ct, fileAad),
            encryptDeterministic: (pt) => this.crypto.encryptDeterministic(fileKey, pt, fileAad),
            hasObject: async () => false,
            sha256: (data) => this.crypto.sha256(data),
        })

        // 2. Build v3 dir tree
        const now = Date.now()
        const dirTreeDeps = { crypto: this.crypto, stateKey, blobId: this.blobId }
        const { rootDirHash, objects: dirObjects } = await buildTreeFromPaths(
            [{
                path: this.filePath,
                entry: {
                    kind: 'file' as const,
                    mime: 'application/json',
                    size: jsonBytes.byteLength,
                    mtime: now,
                    ctime: now,
                    content: packed.content,
                },
            }],
            dirTreeDeps,
        )

        // 3. Build state (file index)
        const wsState: WorkspaceState = { type: 'secureblob-state-v3', rootDirHash }

        const { ciphertext: stateBytes, hash: stateHash } = await encryptState(wsState, {
            crypto: this.crypto,
            stateKey,
            blobId: this.blobId,
        })

        // 3. Build signed commit
        const { commitBytes, commitHash } = await buildSignedCommit({
            blobId: this.blobId,
            parents,
            stateHash,
            writerSeed: this.writerSeed,
            crypto: this.crypto,
        })

        // 4. Push all objects + update head via transaction
        const tx = repo.transaction(this.blobId)
        for (const [hash, bytes] of dirObjects) {
            tx.put(hash, bytes)
        }
        for (const obj of packed.objects) {
            tx.put(obj.hash, obj.bytes)
        }
        tx.put(stateHash, stateBytes)
        tx.put(commitHash, commitBytes)

        // §7.4.4: Build referencedObjects for server-side GC
        const referencedObjects = [stateHash, ...[...dirObjects.keys()], ...packed.objects.map(o => o.hash)]

        const result = await tx.commit({ base: parents, new: commitHash, referencedObjects })

        if (!result.ok) {
            throw new Error(`Config blob commit failed: ${!result.ok ? result.code : 'unknown'} — CAS_MISMATCH`)
        }

        return commitHash
    }
}
